import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, useParams } from "@tanstack/react-router";
import { Edit05, PasscodeLock, ShieldTick } from "@untitledui/icons";
import { useEffect, useId, useRef, useState } from "react";
import { Switch as AriaSwitch } from "react-aria-components";
import { toast } from "sonner";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { HintText } from "@/components/base/input/hint-text";
import { Input } from "@/components/base/input/input";
import { Label } from "@/components/base/input/label";
import type { SelectItemType } from "@/components/base/select/select";
import { Select } from "@/components/base/select/select";
import { useAppForm } from "@/components/form";
import { FieldErrors } from "@/components/form/fields/FieldErrors";
import { Toast } from "@/components/Molecules/Toast/Toast";
import { TableHeader } from "@/components/Organisms/TableHeader/TableHeader";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { authQueryOptions } from "@/services/auth";
import {
	organizationQueryOptions,
	useUpdateOrganization,
	useUpdateOrganizationOAuthInfo,
} from "@/services/organization";

const AUTH_METHOD_LOCAL = "localAuthentication";
const AUTH_METHOD_THIRD_PARTY = "thirdPartyAuthentication";

type AuthenticationMethod =
	| typeof AUTH_METHOD_LOCAL
	| typeof AUTH_METHOD_THIRD_PARTY;

interface IdentityProviderFormValues {
	authenticationMethod: AuthenticationMethod;
	clientId: string;
	clientSecret: string;
	authorizeUrl: string;
	tokenUrl: string;
}

function validateIdentityProviderForm(
	values: IdentityProviderFormValues,
): string | undefined {
	if (values.authenticationMethod === AUTH_METHOD_THIRD_PARTY) {
		if (!values.clientId?.trim()) return "Client ID is required";
		if (!values.clientSecret?.trim()) return "Client Secret is required";
		if (!values.authorizeUrl?.trim()) return "Authorize URL is required";
		if (!values.tokenUrl?.trim()) return "Token URL is required";
	}
	return undefined;
}

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/settings/security",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/settings/security",
	});
	const [isEditingIdentityProvider, setIsEditingIdentityProvider] =
		useState(false);
	const organization = useQuery(organizationQueryOptions.detail(params.orgId));
	const organizationOAuth = useQuery(
		organizationQueryOptions.oauth.detail(params.orgId),
	);
	const updateOAuthInfo = useUpdateOrganizationOAuthInfo();
	const toastId = useId();

	const canUpdateOrganizationOAuthInfo = useQuery({
		...authQueryOptions.checkAction({
			name: "UpdateOrganizationOAuthInfo",
			entityType: "Organization",
			entityId: params.orgId,
		}),
		enabled: !!params.orgId,
	});
	const canEditOAuth = canUpdateOrganizationOAuthInfo.data === true;
	const canEditOAuthRef = useRef(canEditOAuth);
	canEditOAuthRef.current = canEditOAuth;

	const canUpdateOrganizationInfo = useQuery({
		...authQueryOptions.checkAction({
			name: "UpdateOrganizationInfo",
			entityType: "Organization",
			entityId: params.orgId,
		}),
		enabled: !!params.orgId,
	});
	const canEditMfa = canUpdateOrganizationInfo.data === true;

	const [isEditingMfa, setIsEditingMfa] = useState(false);
	const [mfaDraft, setMfaDraft] = useState(false);
	const updateOrganization = useUpdateOrganization();

	const handleEnterMfaEdit = () => {
		if (!canEditMfa) return;
		setMfaDraft(
			!!(organization.data as Record<string, unknown> | undefined)?.mfaEnabled,
		);
		setIsEditingMfa(true);
	};

	const handleSaveMfa = async () => {
		if (!canEditMfa) return;
		try {
			await updateOrganization.mutateAsync({
				id: params.orgId,
				data: { mfaRequired: mfaDraft },
			});
			toast.custom(
				(tHandle) => (
					<Toast
						id={toastId}
						color="success"
						addonText="Success"
						message={t("org-admin.settings.security.mfa.updateSuccess")}
						showCloseButton
						onClose={() => toast.dismiss(tHandle)}
					/>
				),
				{ duration: 5000 },
			);
			setIsEditingMfa(false);
		} catch {
			toast.custom(
				(tHandle) => (
					<Toast
						id={toastId}
						color="error"
						addonText="Error"
						message={t("org-admin.settings.security.mfa.updateError")}
						showCloseButton
						onClose={() => toast.dismiss(tHandle)}
					/>
				),
				{ duration: 5000 },
			);
		}
	};

	const oAuth =
		organizationOAuth.data ?? organization.data?.oAuthProvider ?? undefined;
	const hasOAuthProvider = organization.data?.oAuthProvider != null;
	const defaultAuthMethod: AuthenticationMethod =
		oAuth?.clientId && oAuth?.authorizeEndpointUrl
			? AUTH_METHOD_THIRD_PARTY
			: AUTH_METHOD_LOCAL;

	const identityProviderForm = useAppForm({
		validators: {
			onSubmit: ({ value }) =>
				validateIdentityProviderForm(value as IdentityProviderFormValues),
		},
		defaultValues: {
			authenticationMethod: defaultAuthMethod,
			clientId: oAuth?.clientId ?? "",
			clientSecret: oAuth?.clientSecret ?? "",
			authorizeUrl: oAuth?.authorizeEndpointUrl ?? "",
			tokenUrl: oAuth?.tokenEndpointUrl ?? "",
		},
		onSubmit: async ({ value }) => {
			if (!canEditOAuthRef.current) return;
			const v = value as IdentityProviderFormValues;
			if (v.authenticationMethod === AUTH_METHOD_THIRD_PARTY) {
				try {
					await updateOAuthInfo.mutateAsync({
						organizationId: params.orgId,
						data: {
							clientId: v.clientId.trim(),
							clientSecret: v.clientSecret.trim(),
							authorizeEndpointUrl: v.authorizeUrl.trim(),
							tokenEndpointUrl: v.tokenUrl.trim(),
						},
					});
					toast.custom(
						(tHandle) => (
							<Toast
								id={toastId}
								color="success"
								addonText="Success"
								message={t(
									"org-admin.settings.security.identityProvider.updateSuccess",
								)}
								showCloseButton={true}
								onClose={() => toast.dismiss(tHandle)}
							/>
						),
						{ duration: 5000 },
					);
					setIsEditingIdentityProvider(false);
				} catch {
					toast.custom(
						(tHandle) => (
							<Toast
								id={toastId}
								color="error"
								addonText="Error"
								message={t(
									"org-admin.settings.security.identityProvider.updateError",
								)}
								showCloseButton={true}
								onClose={() => toast.dismiss(tHandle)}
							/>
						),
						{ duration: 5000 },
					);
				}
			} else {
				setIsEditingIdentityProvider(false);
			}
		},
	});

	const authMethodItems: SelectItemType[] = [
		{
			id: AUTH_METHOD_LOCAL,
			label: t(
				"org-admin.settings.security.identityProvider.localAuthentication",
			),
		},
		{
			id: AUTH_METHOD_THIRD_PARTY,
			label: t(
				"org-admin.settings.security.identityProvider.thirdPartyAuthentication",
			),
		},
	];

	const handleCancel = () => {
		identityProviderForm.reset();
		setIsEditingIdentityProvider(false);
	};

	// Sync form values when opening edit so we show current org data
	useEffect(() => {
		if (!isEditingIdentityProvider || !organization.data) return;
		const o = organization.data.oAuthProvider;
		const method: AuthenticationMethod =
			o?.clientId && o?.authorizeEndpointUrl
				? AUTH_METHOD_THIRD_PARTY
				: AUTH_METHOD_LOCAL;
		identityProviderForm.setFieldValue("authenticationMethod", method);
		identityProviderForm.setFieldValue("clientId", o?.clientId ?? "");
		identityProviderForm.setFieldValue("clientSecret", o?.clientSecret ?? "");
		identityProviderForm.setFieldValue(
			"authorizeUrl",
			o?.authorizeEndpointUrl ?? "",
		);
		identityProviderForm.setFieldValue("tokenUrl", o?.tokenEndpointUrl ?? "");
	}, [isEditingIdentityProvider, organization.data, identityProviderForm]);

	return (
		<div className="space-y-0">
			<div className="space-y-4 mt-4">
				<TableHeader
					title={t("org-admin.settings.security.table.title")}
					description={t("org-admin.settings.security.table.description")}
				/>

				{/* Identity Provider card */}
				<div className="bg-white rounded-lg border border-gray-200 mx-6">
					<div className="flex items-center justify-between p-2 bg-gray-50">
						<div className="flex items-center">
							<div className="flex h-10 w-10 items-center justify-center rounded-lg">
								<ShieldTick className="size-5 " />
							</div>
							<h3 className="text-base font-semibold text-gray-900">
								{t("org-admin.settings.security.identityProvider.title")}
							</h3>
						</div>
						{canEditOAuth && !isEditingIdentityProvider && (
							<ButtonUtility
								icon={Edit05}
								color="secondary"
								size="sm"
								onClick={() => setIsEditingIdentityProvider(true)}
								tooltip={t("common.actions.edit")}
								data-testid="org-security-identity-edit-btn"
							/>
						)}
					</div>
					<div className="pt-4 border-t border-gray-200 p-4">
						{!(isEditingIdentityProvider && canEditOAuth) ? (
							<div className="flex items-center gap-24">
								<p className="text-sm font-medium text-tertiary">
									{t(
										"org-admin.settings.security.identityProvider.authenticationMethod",
									)}
								</p>
								<p className="text-md font-medium text-gray-900">
									{!organization.data?.oAuthProvider ||
									!organization.data?.oAuthProviderId
										? t(
												"org-admin.settings.security.identityProvider.localAuthentication",
											)
										: t(
												"org-admin.settings.security.identityProvider.thirdPartyAuthentication",
											)}
								</p>
							</div>
						) : (
							<form
								onSubmit={(e) => {
									e.preventDefault();
									e.stopPropagation();
									identityProviderForm.handleSubmit();
								}}
								className="space-y-4"
							>
								<identityProviderForm.AppField name="authenticationMethod">
									{(field) => (
										<>
											<div className="flex items-center gap-24">
												<Label className="text-tertiary w-48">
													{t(
														"org-admin.settings.security.identityProvider.authenticationMethod",
													)}
												</Label>
												<Select
													label={undefined}
													items={authMethodItems}
													placeholder={t(
														"org-admin.settings.security.identityProvider.authenticationMethod",
													)}
													selectedKey={field.state.value}
													onSelectionChange={(key) =>
														field.handleChange(
															(key as AuthenticationMethod) ??
																AUTH_METHOD_LOCAL,
														)
													}
													onBlur={field.handleBlur}
													name={field.name}
													showSupportingTextInValue={false}
													className="w-[400px]"
													isDisabled={hasOAuthProvider}
													data-testid="org-security-identity-method-select"
												>
													{(item) => (
														<Select.Item
															id={item.id}
															data-testid={`org-security-identity-method-option-${item.id}`}
														>
															{item.label}
														</Select.Item>
													)}
												</Select>
											</div>
											<div className="flex items-center">
												<p className="text-sm text-gray-500 max-w-[400px] ml-72">
													{field.state.value === AUTH_METHOD_THIRD_PARTY
														? t(
																"org-admin.settings.security.identityProvider.descriptionThirdParty",
															)
														: t(
																"org-admin.settings.security.identityProvider.descriptionLocal",
															)}
												</p>
											</div>
											{
												<div className="ml-72 max-w-[560px]">
													<p className="text-sm text-warning-700">
														<span className="font-bold">
															{t(
																"org-admin.settings.security.identityProvider.externalAuthWarningImportant",
															)}
														</span>{" "}
														{t(
															"org-admin.settings.security.identityProvider.externalAuthWarningBody",
														)}
													</p>
												</div>
											}
										</>
									)}
								</identityProviderForm.AppField>

								<identityProviderForm.Subscribe
									selector={(state) => state.values.authenticationMethod}
								>
									{(authenticationMethod) =>
										authenticationMethod === AUTH_METHOD_THIRD_PARTY ? (
											<div className="space-y-4 pt-2">
												<identityProviderForm.AppField
													name="clientId"
													validators={{
														onSubmit: ({ value, fieldApi }) => {
															if (
																fieldApi.form.getFieldValue(
																	"authenticationMethod",
																) !== AUTH_METHOD_THIRD_PARTY
															)
																return undefined;
															if (!String(value ?? "").trim())
																return t(
																	"org-admin.settings.security.identityProvider.clientIdRequired",
																);
															return undefined;
														},
													}}
												>
													{(field) => (
														<div className="space-y-1.5 flex gap-24">
															<Label isRequired className="w-48">
																{t(
																	"org-admin.settings.security.identityProvider.clientId",
																)}
															</Label>
															<div>
																<Input
																	id={field.name}
																	name={field.name}
																	data-testid="org-security-identity-client-id-input"
																	value={field.state.value}
																	onChange={(e) =>
																		field.handleChange(
																			typeof e === "string"
																				? e
																				: ((
																						e as React.ChangeEvent<HTMLInputElement>
																					).target?.value ?? ""),
																		)
																	}
																	onBlur={field.handleBlur}
																	placeholder={t(
																		"org-admin.settings.security.identityProvider.clientIdPlaceholder",
																	)}
																	isInvalid={
																		field.state.meta.isTouched &&
																		!field.state.meta.isValid
																	}
																	className="w-[400px]"
																/>
																<HintText
																	isInvalid={
																		field.state.meta.isTouched &&
																		!field.state.meta.isValid
																	}
																	className="text-xs"
																>
																	{t(
																		"org-admin.settings.security.identityProvider.clientIdHint",
																	)}
																</HintText>
																<FieldErrors meta={field.state.meta} />
															</div>
														</div>
													)}
												</identityProviderForm.AppField>
												<identityProviderForm.AppField
													name="clientSecret"
													validators={{
														onSubmit: ({ value, fieldApi }) => {
															if (
																fieldApi.form.getFieldValue(
																	"authenticationMethod",
																) !== AUTH_METHOD_THIRD_PARTY
															)
																return undefined;
															if (!String(value ?? "").trim())
																return t(
																	"org-admin.settings.security.identityProvider.clientSecretRequired",
																);
															return undefined;
														},
													}}
												>
													{(field) => (
														<div className="space-y-1.5 flex gap-24">
															<Label isRequired className="w-48">
																{t(
																	"org-admin.settings.security.identityProvider.clientSecret",
																)}
															</Label>
															<div>
																<Input
																	id={field.name}
																	name={field.name}
																	type="password"
																	autoComplete="off"
																	data-testid="org-security-identity-client-secret-input"
																	value={field.state.value}
																	onChange={(e) =>
																		field.handleChange(
																			typeof e === "string"
																				? e
																				: ((
																						e as React.ChangeEvent<HTMLInputElement>
																					).target?.value ?? ""),
																		)
																	}
																	onBlur={field.handleBlur}
																	placeholder={t(
																		"org-admin.settings.security.identityProvider.clientSecretPlaceholder",
																	)}
																	isInvalid={
																		field.state.meta.isTouched &&
																		!field.state.meta.isValid
																	}
																	className="w-[400px]"
																/>
																<HintText
																	isInvalid={
																		field.state.meta.isTouched &&
																		!field.state.meta.isValid
																	}
																	className="text-xs"
																>
																	{t(
																		"org-admin.settings.security.identityProvider.clientSecretHint",
																	)}
																</HintText>
																<FieldErrors meta={field.state.meta} />
															</div>
														</div>
													)}
												</identityProviderForm.AppField>
												<identityProviderForm.AppField
													name="authorizeUrl"
													validators={{
														onSubmit: ({ value, fieldApi }) => {
															if (
																fieldApi.form.getFieldValue(
																	"authenticationMethod",
																) !== AUTH_METHOD_THIRD_PARTY
															)
																return undefined;
															const trimmed = String(value ?? "").trim();
															if (!trimmed)
																return t(
																	"org-admin.settings.security.identityProvider.authorizeUrlRequired",
																);
															try {
																new URL(trimmed);
																return undefined;
															} catch {
																return t(
																	"org-admin.settings.security.identityProvider.invalidUrl",
																);
															}
														},
													}}
												>
													{(field) => (
														<div className="space-y-1.5 flex gap-24">
															<Label isRequired className="w-48">
																{t(
																	"org-admin.settings.security.identityProvider.authorizeUrl",
																)}
															</Label>
															<div>
																<Input
																	id={field.name}
																	name={field.name}
																	data-testid="org-security-identity-authorize-url-input"
																	value={field.state.value}
																	onChange={(e) =>
																		field.handleChange(
																			typeof e === "string"
																				? e
																				: ((
																						e as React.ChangeEvent<HTMLInputElement>
																					).target?.value ?? ""),
																		)
																	}
																	onBlur={field.handleBlur}
																	placeholder={t(
																		"org-admin.settings.security.identityProvider.authorizeUrlPlaceholder",
																	)}
																	isInvalid={
																		field.state.meta.isTouched &&
																		!field.state.meta.isValid
																	}
																	className="w-[400px]"
																/>
																<HintText
																	isInvalid={
																		field.state.meta.isTouched &&
																		!field.state.meta.isValid
																	}
																	className="text-xs"
																>
																	{t(
																		"org-admin.settings.security.identityProvider.authorizeUrlHint",
																	)}
																</HintText>
																<FieldErrors meta={field.state.meta} />
															</div>
														</div>
													)}
												</identityProviderForm.AppField>
												<identityProviderForm.AppField
													name="tokenUrl"
													validators={{
														onSubmit: ({ value, fieldApi }) => {
															if (
																fieldApi.form.getFieldValue(
																	"authenticationMethod",
																) !== AUTH_METHOD_THIRD_PARTY
															)
																return undefined;
															const trimmed = String(value ?? "").trim();
															if (!trimmed)
																return t(
																	"org-admin.settings.security.identityProvider.tokenUrlRequired",
																);
															try {
																new URL(trimmed);
																return undefined;
															} catch {
																return t(
																	"org-admin.settings.security.identityProvider.invalidUrl",
																);
															}
														},
													}}
												>
													{(field) => (
														<div className="space-y-1.5 flex gap-24">
															<Label isRequired className="w-48">
																{t(
																	"org-admin.settings.security.identityProvider.tokenUrl",
																)}
															</Label>
															<div>
																<Input
																	id={field.name}
																	name={field.name}
																	data-testid="org-security-identity-token-url-input"
																	value={field.state.value}
																	onChange={(e) =>
																		field.handleChange(
																			typeof e === "string"
																				? e
																				: ((
																						e as React.ChangeEvent<HTMLInputElement>
																					).target?.value ?? ""),
																		)
																	}
																	onBlur={field.handleBlur}
																	placeholder={t(
																		"org-admin.settings.security.identityProvider.tokenUrlPlaceholder",
																	)}
																	isInvalid={
																		field.state.meta.isTouched &&
																		!field.state.meta.isValid
																	}
																	className="w-[400px]"
																/>
																<HintText
																	isInvalid={
																		field.state.meta.isTouched &&
																		!field.state.meta.isValid
																	}
																	className="text-xs"
																>
																	{t(
																		"org-admin.settings.security.identityProvider.tokenUrlHint",
																	)}
																</HintText>
																<FieldErrors meta={field.state.meta} />
															</div>
														</div>
													)}
												</identityProviderForm.AppField>
											</div>
										) : null
									}
								</identityProviderForm.Subscribe>

								<identityProviderForm.Subscribe
									selector={(state) => state.errors}
								>
									{(errors) =>
										errors && errors.length > 0 ? (
											<div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
												<p className="text-sm text-red-800">
													{Array.isArray(errors)
														? errors.join(", ")
														: String(errors[0])}
												</p>
											</div>
										) : null
									}
								</identityProviderForm.Subscribe>

								<div className="flex justify-end gap-2  pt-2">
									<Button
										type="button"
										onClick={handleCancel}
										color="secondary"
										data-testid="org-security-identity-cancel-btn"
									>
										{t("common.actions.cancelEdits")}
									</Button>
									<identityProviderForm.Subscribe
										selector={(state) => [state.canSubmit, state.isSubmitting]}
									>
										{([canSubmit, isSubmitting]) => (
											<Button
												type="submit"
												disabled={!canSubmit || isSubmitting}
												data-testid="org-security-identity-save-btn"
											>
												{isSubmitting
													? "Saving…"
													: t("common.actions.saveEdits")}
											</Button>
										)}
									</identityProviderForm.Subscribe>
								</div>
							</form>
						)}
					</div>
				</div>

				{/* MFA card */}
				<div className="bg-white rounded-lg border border-gray-200 mx-6">
					<div className="flex items-center justify-between p-2 bg-gray-50">
						<div className="flex items-center">
							<div className="flex h-10 w-10 items-center justify-center rounded-lg">
								<PasscodeLock className="size-5" />
							</div>
							<h3 className="text-base font-semibold text-gray-900">
								{t("org-admin.settings.security.mfa.title")}
							</h3>
						</div>
						{canEditMfa && !isEditingMfa && (
							<ButtonUtility
								icon={Edit05}
								color="secondary"
								size="sm"
								onClick={handleEnterMfaEdit}
								tooltip={t("common.actions.edit")}
								data-testid="org-security-mfa-edit-btn"
							/>
						)}
					</div>
					<div className="pt-4 border-t border-gray-200 p-4">
						{!(isEditingMfa && canEditMfa) ? (
							<div className="flex items-center gap-24">
								<p className="text-sm font-medium text-tertiary">
									{t("org-admin.settings.security.mfa.enableLabel")}
								</p>
								<Badge
									color={
										(organization.data as Record<string, unknown> | undefined)
											?.mfaEnabled
											? "success"
											: "gray"
									}
								>
									{(organization.data as Record<string, unknown> | undefined)
										?.mfaEnabled
										? t("org-admin.settings.security.mfa.enabled")
										: t("org-admin.settings.security.mfa.disabled")}
								</Badge>
							</div>
						) : (
							<div className="space-y-4">
								<div className="flex items-center gap-24">
									<p className="text-sm font-medium text-tertiary">
										{t("org-admin.settings.security.mfa.enableLabel")}
									</p>
									<AriaSwitch
										isSelected={mfaDraft}
										onChange={setMfaDraft}
										data-testid="org-security-mfa-switch"
										aria-label={t(
											"org-admin.settings.security.mfa.enableLabel",
										)}
										className="group inline-flex items-center gap-2 outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-full"
									>
										<span
											className="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors group-selected:bg-brand-500 bg-bg-quaternary group-data-disabled:cursor-not-allowed group-data-disabled:opacity-50"
											aria-hidden
										>
											<span className="inline-block size-4 transform rounded-full bg-white shadow transition-transform translate-x-0.5 group-selected:translate-x-4" />
										</span>
									</AriaSwitch>
								</div>
								<div className="flex justify-end gap-2 pt-2">
									<Button
										type="button"
										onClick={() => setIsEditingMfa(false)}
										color="secondary"
									>
										{t("common.actions.cancelEdits")}
									</Button>
									<Button
										type="button"
										onClick={() => void handleSaveMfa()}
										isLoading={updateOrganization.isPending}
										showTextWhileLoading
										data-testid="org-security-mfa-save-btn"
									>
										{t("common.actions.saveEdits")}
									</Button>
								</div>
							</div>
						)}
					</div>
				</div>

				<Outlet />
			</div>
		</div>
	);
}
